import { useCallback, useMemo, useRef, useState } from 'react'

import {
    ClientRect,
    Collision,
    CollisionDescriptor,
    CollisionDetection,
    DragOverEvent,
    DragStartEvent,
    getFirstCollision,
    MouseSensor,
    pointerWithin,
    rectIntersection,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { snapCenterToCursor } from '@dnd-kit/modifiers'
import { Coordinates } from '@dnd-kit/utilities'

import { DraggableTypes, Widget } from 'features/views/LayoutEditor/types'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    createWidget,
    generateWidgetId,
    getWidgetAtPath,
    getYWidgetAtPath,
    insertYWidgetAtPath,
    moveWidgetToPath,
} from 'features/views/LayoutEditor/utils'

import useDebounce from 'v2/ui/utils/useDebounce'

import { LayoutEditorDnDContextValue, LayoutEditorDnDDragTarget } from './useLayoutEditorDnDContext'

const DRAG_OVER_DEBOUNCE_RATE = 100

export function useLayoutEditorDnDContextState() {
    const { commands, schema, view, object, fields } = useLayoutEditorContext()
    const viewRef = useRef(view)
    viewRef.current = view
    const schemaRef = useRef(schema)
    schemaRef.current = schema
    const objectRef = useRef(object)
    objectRef.current = object
    const fieldsRef = useRef(fields)
    fieldsRef.current = fields

    const wrapperRef = useRef<HTMLDivElement>(null)

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                // This lets us select widgets on click, but still keeps the dragging functionality working in the same way.
                distance: 5,
            },
        })
    )

    const [activeDragWidget, setActiveDragWidget] = useState<Widget | undefined>()
    const activeDragWidgetRef = useRef(activeDragWidget)
    activeDragWidgetRef.current = activeDragWidget

    const [initialDragTarget, setInitialDragTarget] = useState<
        LayoutEditorDnDDragTarget | undefined
    >()
    const initialDragTargetRef = useRef(initialDragTarget)
    initialDragTargetRef.current = initialDragTarget

    const [destinationDragTarget, setDestinationDragTarget] = useState<
        LayoutEditorDnDDragTarget | undefined
    >()
    const destinationDragTargetRef = useRef(destinationDragTarget)
    destinationDragTargetRef.current = destinationDragTarget

    const contextValue: LayoutEditorDnDContextValue = useMemo(
        () => ({
            activeDragWidget,
            initialDragTarget,
            destinationDragTarget,
        }),
        [activeDragWidget, initialDragTarget, destinationDragTarget]
    )

    const draggingWidgetSchema = useMemo(() => {
        return activeDragWidget ? schemaRef.current.widgets[activeDragWidget.type] : undefined
    }, [activeDragWidget])

    const onDragStart = useCallback(
        (event: DragStartEvent) => {
            const widgetId = event.active.id as string
            const initialPath = event.active.data.current?.path ?? []
            const initialIdx = event.active.data.current?.sortable?.index ?? 0

            const widgetType = event.active.data.current?.widgetType ?? ''
            if (!widgetType) return

            const view = viewRef.current
            if (!view) return

            const object = objectRef.current
            const fields = fieldsRef.current

            let widget: Widget | undefined
            if (event.active.data.current?.isFromPicker) {
                widget = createWidget(widgetType, schemaRef.current, view, object, fields)
                if (!widget) return
                widget.id = widgetId
            } else {
                // If the drag is not from the widget picker, then the drag is from a widget area.
                widget = getWidgetAtPath(initialPath, widgetId, view.layout)
                if (!widget) return

                setInitialDragTarget({
                    path: initialPath,
                    idx: initialIdx,
                })
            }

            setActiveDragWidget(widget)

            // Deselect the widget to prevent weird behavior when dragging from the widget picker.
            commands.deselectWidget()

            wrapperRef.current?.style.setProperty('cursor', 'grabbing')
        },
        [commands]
    )

    const onDragOver = useCallback((event: DragOverEvent) => {
        if (!activeDragWidgetRef.current) return

        const destinationWidgetArea = getDestinationWidgetArea(event)
        if (!destinationWidgetArea) return

        requestAnimationFrame(() => {
            setDestinationDragTarget({
                path: destinationWidgetArea.path,
                idx: destinationWidgetArea.idx,
            })
        })
    }, [])

    const debouncedDragOver = useDebounce(onDragOver, DRAG_OVER_DEBOUNCE_RATE)

    const onDragEnd = useCallback(() => {
        const cleanup = () => {
            wrapperRef.current?.style.removeProperty('cursor')

            setActiveDragWidget(undefined)
            setInitialDragTarget(undefined)
            setDestinationDragTarget(undefined)
        }

        const activeDragWidget = activeDragWidgetRef.current
        const destinationDragTarget = destinationDragTargetRef.current
        const initialDragTarget = initialDragTargetRef.current

        if (!activeDragWidget || !destinationDragTarget) {
            cleanup()

            return
        }

        const destinationIdx = destinationDragTarget.idx

        // The path of the widget being dragged.
        const initialPath = initialDragTarget?.path ?? []
        const destinationPath = destinationDragTarget.path

        const needsCreation = !initialDragTarget
        commands.transaction((data) => {
            if (needsCreation) {
                // If the widget is being dragged from the widget picker, we need to create it.
                // We just have a temporary id for the widget, so we need to generate a new one.
                const newId = generateWidgetId()

                const { widget: existingWidget } = getYWidgetAtPath(initialPath, newId, data)
                // Something went wrong, so we don't need to create a new widget.
                if (existingWidget) return

                insertYWidgetAtPath(
                    destinationPath,
                    schemaRef.current,
                    {
                        ...activeDragWidget,
                        id: newId,
                    },
                    data,
                    destinationIdx,
                    objectRef.current,
                    fieldsRef.current
                )

                // Update the selected widget, since the path might have changed.
                commands.selectWidgetAtPath(newId, destinationPath)
            } else {
                moveWidgetToPath(
                    activeDragWidget.id,
                    initialPath,
                    destinationPath,
                    destinationIdx,
                    data
                )

                // Update the selected widget, since the path might have changed.
                commands.selectWidgetAtPath(activeDragWidget.id, destinationPath)
            }
        })
        cleanup()
    }, [commands])

    const onDragCancel = useCallback(() => {
        const activeDragWidget = activeDragWidgetRef.current
        const initialDragTarget = initialDragTargetRef.current

        // Reselect widget.
        if (activeDragWidget && initialDragTarget) {
            commands.selectWidgetAtPath(activeDragWidget.id, initialDragTarget.path)
        }

        wrapperRef.current?.style.removeProperty('cursor')

        setActiveDragWidget(undefined)
        setInitialDragTarget(undefined)
        setDestinationDragTarget(undefined)
    }, [commands])

    const lastCollisionRef = useRef<Collision[]>([])
    const collisionDetection: CollisionDetection = useCallback((args) => {
        const pointerIntersections = pointerWithin(args)
        const intersections =
            pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args)

        const overCollision = getFirstCollision(intersections)
        if (!overCollision) return lastCollisionRef.current

        const droppableContainerData = overCollision.data?.droppableContainer.data?.current
        const isWidget = droppableContainerData?.type === DraggableTypes.Widget
        const isWidgetArea = droppableContainerData?.type === DraggableTypes.WidgetArea

        if (isWidget) {
            const currentContainerId = droppableContainerData?.sortable?.containerId
            const droppablesInContainer = args.droppableContainers.filter((container) => {
                return container.data?.current?.sortable?.containerId === currentContainerId
            })

            // Check how the pointer is positioned relative to the widget.
            const collisionsInContainer = closestCornersToPointer({
                ...args,
                droppableContainers: droppablesInContainer,
            })
            if (collisionsInContainer.length > 0) {
                lastCollisionRef.current = [{ id: collisionsInContainer[0].id }]
            }
        } else if (isWidgetArea) {
            // If the pointer is over an empty or out of bounds part of a widget area, we can return the widget area.
            lastCollisionRef.current = [overCollision]
        }

        return lastCollisionRef.current
    }, [])

    const dragOverlayModifiers = useMemo(() => {
        return [snapCenterToCursor]
    }, [])

    const autoScroll = useMemo(() => {
        return {
            acceleration: 30,
            // Disable horizontal auto-scrolling.
            threshold: { x: 0, y: 0.2 },
            layoutShiftCompensation: false,
        }
    }, [])

    const dragOverlayStyle = useMemo(() => {
        return {
            width: '140px',
            height: '75px',
        }
    }, [])

    return useMemo(
        () => ({
            sensors,
            onDragStart,
            onDragOver: debouncedDragOver,
            onDragEnd,
            onDragCancel,
            draggingWidgetSchema,
            wrapperRef,
            collisionDetection,
            contextValue,
            dragOverlayModifiers,
            autoScroll,
            dragOverlayStyle,
        }),
        [
            sensors,
            onDragStart,
            debouncedDragOver,
            onDragEnd,
            onDragCancel,
            draggingWidgetSchema,
            collisionDetection,
            contextValue,
            dragOverlayModifiers,
            autoScroll,
            dragOverlayStyle,
        ]
    )
}

function getDestinationWidgetArea(event: DragOverEvent):
    | {
          id: string
          path: string[]
          idx: number
      }
    | undefined {
    const { over, delta, activatorEvent, collisions } = event
    if (!over || over.disabled) return undefined

    if (over.data.current?.type === DraggableTypes.WidgetArea) {
        // We are dragging over an empty part of a widget area (can be before or after the widgets, or in an empty widget area).
        const childrenCount = over.data.current.childrenCount ?? 0
        if (childrenCount === 0) {
            return {
                id: over.data.current.id,
                path: over.data.current.path,
                idx: 0,
            }
        }

        // Determine the index of the widget that the pointer is closest to.
        const containerElement = getFirstCollision(collisions)?.data?.droppableContainer?.node
            ?.current as HTMLElement | null
        if (!containerElement) return undefined

        const lastChild = containerElement.children[childrenCount - 1] as HTMLElement | null
        const lastChildY = lastChild?.offsetTop ?? 0

        const mouseY = (activatorEvent as MouseEvent).clientY + delta.y
        const idx = mouseY >= lastChildY ? childrenCount : 0

        return {
            id: over.data.current.id,
            path: over.data.current.path,
            idx,
        }
    }

    if (over.data.current) {
        const idx = over.data.current.sortable?.index
        if (typeof idx !== 'number') return undefined

        const path = over.data.current.path
        const id = over.data.current.path.at(-1)

        // We are dragging over a widget in a widget area.
        return {
            id,
            path,
            idx,
        }
    }

    return undefined
}

const closestCornersToPointer: CollisionDetection = ({
    droppableRects,
    droppableContainers,
    pointerCoordinates,
}) => {
    if (!pointerCoordinates) {
        return []
    }

    const collisions: CollisionDescriptor[] = []

    for (const droppableContainer of droppableContainers) {
        const { id } = droppableContainer
        const rect = droppableRects.get(id)

        if (rect) {
            if (isPointWithinRect(pointerCoordinates, rect)) {
                // The pointer is within the rect, so we can return the rect.
                collisions.push({
                    id,
                    data: { droppableContainer, value: 0 },
                })
            } else {
                const rectCorners = cornersOfRectangle(rect)
                const distances = rectCorners.reduce((accumulator, corner) => {
                    return accumulator + distanceBetween(pointerCoordinates, corner)
                }, 0)
                const effectiveDistance = Number((distances / 4).toFixed(4))

                collisions.push({
                    id,
                    data: { droppableContainer, value: effectiveDistance },
                })
            }
        }
    }

    return collisions.sort((a, b) => {
        return a.data.value - b.data.value
    })
}

function isPointWithinRect(point: Coordinates, rect: ClientRect): boolean {
    const { top, left, bottom, right } = rect

    return top <= point.y && point.y <= bottom && left <= point.x && point.x <= right
}

function distanceBetween(point1: Coordinates, point2: Coordinates) {
    return Math.sqrt((point1.x - point2.x) ** 2 + (point1.y - point2.y) ** 2)
}

function cornersOfRectangle({ left, top, height, width }: ClientRect) {
    return [
        {
            x: left,
            y: top,
        },
        {
            x: left + width,
            y: top,
        },
        {
            x: left,
            y: top + height,
        },
        {
            x: left + width,
            y: top + height,
        },
    ]
}
